// File: frontend/src/hooks/usePersonEntries.ts
// src/hooks/usePersonEntries.ts

import { useState, useCallback, useEffect, useRef } from "react";
import { EntryModel } from "../models";
import { fetchEntriesByPersonId } from "../services/personEntryService";
import { ENTRIES_PER_PAGE } from "../constants";
import { useAuth } from "../context/AuthContext";

/**
 * initialPage — the page to load, taken from the "?sayfa=" URL parameter by the
 * page component (see useUrlPage). Kept in the URL rather than in state so a
 * page can be linked to, survives a refresh, and gives search engines one
 * address per page instead of one per topic.
 *
 * Changing any filter resets to page 1 on purpose: page 7 of an unfiltered list
 * has nothing to do with page 7 of a filtered one, and landing on an empty page
 * would look like a fault. The page component clears the URL parameter to match.
 */
export const usePersonEntries = (
  personId: number | null,
  showRookieEntries: boolean = true,
  sort: string = "oldest",
  entriesPerPage: number = ENTRIES_PER_PAGE,
  filterToday: boolean = false,
  rookieOnly: boolean = false,
  keyword: string = "",
  initialPage: number = 1
) => {
  const { user } = useAuth();

  const [entries, setEntries]         = useState<EntryModel[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [totalItems, setTotalItems]   = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Ticket 1c: sequence number of the most recently STARTED entry request.
  //
  // Every fetch takes a copy of this counter before it awaits and compares it
  // again afterwards. If the counter moved on in the meantime a newer request
  // has been fired — normally because the reader clicked another person in the
  // left pane — and this response is stale, so it must not be written to state.
  //
  // Without this guard the responses are applied in arrival order rather than
  // request order: a slow response for the previously open person landing after
  // the fast response for the current one overwrites the list, and the page
  // ends up showing one person's header above another person's entries.
  const requestIdRef = useRef(0);

  // Mirrors initialPage so the effect below can read it without adding a
  // dependency that would refetch on every unrelated render.
  const initialPageRef = useRef(initialPage);
  initialPageRef.current = initialPage;

  const loadEntries = useCallback(async (page: number = 1) => {
    // Ticket 1c: claim a sequence number for this request before anything else.
    const requestId = ++requestIdRef.current;
    if (!personId) { setEntries([]); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchEntriesByPersonId(personId, page, entriesPerPage, showRookieEntries, sort, filterToday, rookieOnly, keyword);
      // Ticket 1c: a newer request was started while this one was in flight —
      // its result is the one the reader is waiting for, so drop this response.
      if (requestId !== requestIdRef.current) return;
      setEntries(res.entries);
      setCurrentPage(res.currentPage);
      setTotalPages(res.totalPages);
      setTotalItems(res.totalItems);
    } catch {
      // A stale failure must not surface either: the current person may well be
      // loading fine, and the error message would replace their entry list.
      if (requestId !== requestIdRef.current) return;
      setError("Entryler yüklenemedi.");
    } finally {
      // Only the newest request owns the loading flag. Clearing it from a stale
      // one would hide the spinner while the real request is still running.
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [personId, showRookieEntries, sort, entriesPerPage, filterToday, rookieOnly, keyword, initialPage]);

  // Ticket 1c: drop the previous person's entries the moment the person changes.
  // The fetch below replaces them anyway, but only once it comes back; until
  // then the old list would still be rendered under the new person's header.
  // Declared before the loading effect so both run in the same commit and the
  // reader never sees an empty-state flash.
  useEffect(() => {
    setEntries([]);
    setTotalItems(0);
    setTotalPages(1);
    setCurrentPage(1);
  }, [personId]);

  useEffect(() => {
    loadEntries(initialPageRef.current);
  }, [personId, user?.id, showRookieEntries, sort, loadEntries]);

  const loadMoreEntries = useCallback(async () => {
    if (!personId || isLoadingMore || currentPage >= totalPages) return;
    // Ticket 1c: "load more" appends, so a stale response is even worse here —
    // it would mix another person's entries into the current list. Remember
    // which request generation this belongs to and verify it after the round trip.
    const requestId = requestIdRef.current;
    setIsLoadingMore(true);
    try {
      const res = await fetchEntriesByPersonId(personId, currentPage + 1, entriesPerPage, showRookieEntries, sort, filterToday, rookieOnly, keyword);
      if (requestId !== requestIdRef.current) return;
      setEntries(prev => [...prev, ...res.entries]);
      setCurrentPage(res.currentPage);
      setTotalPages(res.totalPages);
    } catch {
      // Silent failure
    } finally {
      setIsLoadingMore(false);
    }
  }, [personId, isLoadingMore, currentPage, totalPages, entriesPerPage, showRookieEntries, sort, filterToday, rookieOnly, keyword]);

  const updateEntries = useCallback(
    (callback: (prev: EntryModel[]) => EntryModel[]) => setEntries(callback),
    []
  );

  return {
    entries, loading, error,
    currentPage, totalPages, totalItems,
    isLoadingMore,
    loadEntries, loadMoreEntries, updateEntries,
  };
};